/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useContext, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useClickAway } from 'react-use';
import LayoutContext from '../context/layout-context';
import DataContext from '../context/data-context';
import { LANGUAGES } from '../constants';

const LANG_KEYS = LANGUAGES.map((l) => l.key.toLowerCase());

// Strip trailing slashes so paths compare consistently with the sitemap,
// which stores paths without a trailing slash.
const normalizePath = (p) => {
  const clean = (p || '/').replace(/\/+$/, '');
  return clean === '' ? '/' : clean;
};

// Fetch and parse the combined sitemap once, caching the promise so repeated
// language switches reuse the same in-memory Set of known paths.
let sitemapPathsPromise = null;
const loadSitemapPaths = () => {
  if (!sitemapPathsPromise) {
    sitemapPathsPromise = fetch('/sitemap/sitemap.xml')
      .then((res) => res.text())
      .then((xml) => {
        const set = new Set();
        (xml.match(/<loc>([\s\S]*?)<\/loc>/g) || []).forEach((entry) => {
          const url = entry.replace(/<\/?loc>/g, '').trim();
          try {
            set.add(normalizePath(new URL(url).pathname));
          } catch (e) {
            // ignore malformed <loc> entries
          }
        });
        return set;
      })
      .catch(() => new Set());
  }
  return sitemapPathsPromise;
};

// On language switch: keep the user on the same page in the target language if
// that page exists in the sitemap, otherwise fall back to the language home.
export const switchLanguage = async (lang) => {
  window.localStorage.setItem('currentLanguage', lang.key);

  const segments = window.location.pathname.split('/').filter(Boolean);
  if (segments.length && LANG_KEYS.includes(segments[0].toLowerCase())) {
    segments.shift(); // drop the current locale prefix
  }
  const rest = segments.length ? `/${segments.join('/')}` : '';

  // Pages are served with a canonical trailing slash; navigating to the
  // slash-less form triggers a 301/302 redirect, which shows up as a double
  // load ("flicker"). Always navigate to the trailing-slash URL to skip it.
  const withTrailingSlash = (p) => (p.endsWith('/') ? p : `${p}/`);

  if (!rest) {
    window.location.href = withTrailingSlash(lang.path); // already on a language home
    return;
  }

  const target = normalizePath(`${lang.path}${rest}`);
  const paths = await loadSitemapPaths();
  window.location.href = withTrailingSlash(
    paths.has(target) ? target : lang.path
  );
};

const SettingsButtons = ({ showLanguages, showContentButton }) => {
  const { theme, setTheme, setMobileLeftNavOpen } = useContext(LayoutContext);
  const {
    path,
    settings: { dictionary },
  } = useContext(DataContext);

  const [themeOpen, setThemeOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const ref = useRef(null);

  useClickAway(ref, () => {
    setThemeOpen(false);
    setLangOpen(false);
  });

  const themes = [
    { key: 'light', label: dictionary.settingsThemeLight },
    { key: 'dark', label: dictionary.settingsThemeDark },
    { key: 'high-contrast', label: dictionary.settingsThemeHighContrast },
  ];

  return (
    <div className="settings-buttons test">
      {showContentButton && (
        <button
          type="button"
          className="button content-btn"
          onClick={() => {
            setMobileLeftNavOpen(true);
          }}
        >
          <i className="icon icon-content" />
          <span className="">{dictionary.navigationMobileContent}</span>
        </button>
      )}
      <ul ref={ref}>
        <li>
          <button
            type="button"
            className="button"
            onClick={() => {
              setThemeOpen(!themeOpen);
              setLangOpen(false);
            }}
          >
            <i className="icon icon-theme" />
            <span className="is-hidden-tablet">{dictionary.settingsTheme}</span>
          </button>
          <button
            type="button"
            className="button"
            onClick={() => {
              window.print();
            }}
          >
            <i className="icon icon-print" />
            <span className="is-hidden-tablet">{dictionary.settingsPrint}</span>
          </button>
          {themeOpen && (
            <div className="popup show">
              <ul>
                {themes.map((t) => (
                  <li
                    onClick={() => {
                      window.localStorage.setItem('continia.theme', t.key);
                      setTheme(t.key);
                    }}
                    key={t.key}
                    className={t.key === theme ? 'active' : ''}
                  >
                    {t.label}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </li>
        {showLanguages && (
          <li>
            <button
              type="button"
              className="button"
              onClick={() => {
                if (!langOpen) loadSitemapPaths(); // warm the cache on open
                setLangOpen(!langOpen);
                setThemeOpen(false);
              }}
            >
              <i className="icon icon-globe" />
              <span className="is-hidden-tablet">
                {dictionary.settingsLanguage}
              </span>
            </button>
            {langOpen && (
              <div className="popup show">
                <ul>
                  {LANGUAGES.map((l) => (
                    <li
                      onClick={() => {
                        switchLanguage(l);
                      }}
                      key={l.key}
                      className={path.startsWith(l.path) ? 'active' : ''}
                    >
                      {l.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </li>
        )}
      </ul>
    </div>
  );
};

SettingsButtons.propTypes = {
  showLanguages: PropTypes.bool,
  showContentButton: PropTypes.bool,
};
SettingsButtons.defaultProps = {
  showLanguages: true,
  showContentButton: true,
};

export default SettingsButtons;
